(() => {
  'use strict';
  if (!document.body.classList.contains('sf-pani')) return;
  window.__sfPaniUI?.destroy();

  const html = document.documentElement;
  const controller = new AbortController();
  const signal = controller.signal;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  const tracks = new Map();
  const stories = new Map();
  const parallaxes = new Map();
  const accordions = new Map();
  const tabsets = new Map();
  const videos = new Map();
  const dialogs = new Map();
  const waiting = new Set();
  const titles = new WeakSet();
  let header;
  let hero;
  let raf = 0;
  let destroyed = false;
  let activeDialog = null;
  let scrollLock = null;
  let tabSequence = 0;
  let videoSequence = 0;
  let introOverlay = null;
  const introTimers = [];

  const clamp = (number, min = 0, max = 1) => Math.min(max, Math.max(min, number));
  const isPaniDialog = element => element?.matches?.('#PaniMenu, #PaniShop, .pani-product-dialog');
  const select = (root, selector) => [
    ...(root instanceof Element && root.matches(selector) ? [root] : []),
    ...root.querySelectorAll(selector)
  ];
  const listen = (target, type, handler, options = {}) => {
    target.addEventListener(type, handler, { ...options, signal });
  };
  const visible = element => {
    waiting.delete(element);
    element.classList.add('is-visible');
    revealObserver?.unobserve(element);
  };
  const revealObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
        if (document.hidden) waiting.add(entry.target);
        else visible(entry.target);
      }
    }
  }, { threshold: [0, 0.15] }) : null;
  const videoObserver = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    for (const entry of entries) {
      const state = videos.get(entry.target);
      if (!state) continue;
      state.inView = entry.isIntersecting && entry.intersectionRatio >= 0.15;
      syncVideo(entry.target, state);
    }
  }, { threshold: [0, 0.15] }) : null;

  function schedule() {
    if (!destroyed && !document.hidden && !raf) raf = requestAnimationFrame(update);
  }

  function wrapTitle(title) {
    if (titles.has(title) || title.querySelector('.pani-word')) return;
    titles.add(title);
    const walker = document.createTreeWalker(title, NodeFilter.SHOW_TEXT, {
      acceptNode(node) {
        return node.textContent.trim() && !node.parentElement.closest('script, style, svg, textarea, [aria-hidden="true"]')
          ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      }
    });
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    let index = 0;
    for (const node of nodes) {
      const fragment = document.createDocumentFragment();
      for (const token of node.textContent.match(/\S+|\s+/gu) || []) {
        if (!token.trim()) fragment.append(document.createTextNode(token));
        else {
          const wrap = document.createElement('span');
          const word = document.createElement('span');
          wrap.className = 'pani-word-wrap';
          word.className = 'pani-word';
          word.style.setProperty('--word-index', index++);
          word.textContent = token;
          wrap.append(word);
          fragment.append(wrap);
        }
      }
      node.replaceWith(fragment);
    }
  }

  function setFrame(story, index) {
    const state = stories.get(story);
    if (!state) return;
    index = clamp(index, 0, state.frames.length - 1);
    if (state.index === index) return;
    state.index = index;
    story.dataset.activeFrame = String(index);
    state.frames.forEach((frame, position) => {
      const active = position === index;
      frame.classList.toggle('is-active', active);
      frame.setAttribute('aria-hidden', String(!active));
      if ('inert' in frame) frame.inert = !active;
    });
    for (const [video, videoState] of videos) {
      if (story.contains(video)) syncVideo(video, videoState);
    }
    state.steps.forEach((step, position) => {
      const number = Number(step.dataset.paniStep);
      const active = (Number.isFinite(number) && step.dataset.paniStep !== '' ? number : position) === index;
      step.classList.toggle('is-active', active);
      step.setAttribute('aria-current', active ? 'step' : 'false');
    });
  }

  function update() {
    raf = 0;
    if (destroyed || document.hidden) return;
    if (header?.isConnected) {
      const bounds = hero?.isConnected ? hero.getBoundingClientRect() : null;
      const progress = bounds ? clamp(-bounds.top / Math.max(1, bounds.height)) : 1;
      const phase = bounds ? clamp((progress - 0.9) / 0.1) : 1;
      const condensed = !bounds || progress >= 0.9;
      const mobile = matchMedia('(max-width: 991px)').matches;
      const navigation = header.querySelector('.pani-nav-links');
      const menuToggle = header.querySelector('[data-pani-menu-toggle]');
      const focused = document.activeElement;
      header.classList.toggle('is-scrolled', condensed);
      if (navigation) navigation.inert = mobile || condensed;
      if (menuToggle) menuToggle.inert = !mobile && !condensed;
      if (navigation?.inert && navigation.contains(focused)) menuToggle?.focus({ preventScroll: true });
      else if (menuToggle?.inert && menuToggle === focused) navigation?.querySelector('a')?.focus({ preventScroll: true });
      header.style.setProperty('--pani-nav-phase', phase.toFixed(4));
      header.style.setProperty('--pani-header-progress', progress.toFixed(4));
    }
    for (const [story, state] of stories) {
      if (!story.isConnected) { stories.delete(story); continue; }
      const bounds = story.getBoundingClientRect();
      const range = bounds.height - innerHeight;
      if (range <= 1 || !state.frames.length) continue;
      const progress = clamp(-bounds.top / range);
      story.style.setProperty('--story-progress', progress.toFixed(4));
      setFrame(story, Math.min(state.frames.length - 1, Math.floor(progress * state.frames.length)));
    }
    let chasing = false;
    if (!reduce.matches) {
      for (const [element, state] of parallaxes) {
        if (!element.isConnected) { parallaxes.delete(element); continue; }
        const bounds = state.anchor.getBoundingClientRect();
        if (bounds.bottom < -40 || bounds.top > innerHeight + 40) continue;
        const progress = clamp((innerHeight - bounds.top) / Math.max(1, innerHeight + bounds.height));
        const horizontal = element.matches('.pani-super');
        const target = horizontal ? -10 * progress : -5 + 10 * progress;
        state.current += (target - state.current) * 0.1;
        if (Math.abs(target - state.current) < 0.005) state.current = target;
        else chasing = true;
        const y = `${state.current.toFixed(3)}%`;
        element.style.translate = horizontal ? `${y} 0` : `0 ${y}`;
        element.style.setProperty('--pani-parallax-y', y);
      }
    }
    for (const [track, state] of tracks) {
      if (!track.isConnected) { destroyTrack(track, state); continue; }
      const max = Math.max(0, track.scrollWidth - track.clientWidth);
      if (state.loop) {
        if (track.clientWidth > 0 && state.loop.width !== track.clientWidth) refreshLoop(track, state);
        promoteLoop(track, state);
      }
      for (const button of state.controls) {
        const disabled = !state.loop && (button.hasAttribute('data-pani-prev') ? track.scrollLeft <= 1 : track.scrollLeft >= max - 1);
        if ('disabled' in button) button.disabled = disabled;
        button.setAttribute('aria-disabled', String(disabled));
      }
    }
    if (chasing) schedule();
  }

  function instantScroll(x, y) {
    const old = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';
    window.scrollTo({ left: x, top: y, behavior: 'instant' });
    html.style.scrollBehavior = old;
  }

  function syncDialogClasses() {
    const menuOpen = Boolean(document.getElementById('PaniMenu')?.open);
    const shopOpen = Boolean(document.getElementById('PaniShop')?.open);
    const productOpen = Boolean(document.querySelector('.pani-product-dialog[open]'));
    html.classList.toggle('pani-menu-open', menuOpen);
    html.classList.toggle('pani-shop-open', shopOpen);
    html.classList.toggle('pani-product-open', productOpen);
    html.classList.toggle('pani-dialog-open', menuOpen || shopOpen || productOpen);
    document.querySelectorAll('[data-pani-menu-toggle]').forEach(button => {
      button.setAttribute('aria-expanded', String(menuOpen));
      button.setAttribute('aria-controls', 'PaniMenu');
    });
    if (!menuOpen && !shopOpen && !productOpen && scrollLock) {
      html.style.overflow = scrollLock.overflow;
      scrollLock = null;
    }
  }

  function finishDialog(dialog, options = {}) {
    const state = dialogs.get(dialog);
    if (!state) return;
    clearTimeout(state.timer);
    state.timer = 0;
    state.closing = false;
    state.options = options;
    dialog.classList.remove('is-closing');
    if (activeDialog === dialog) activeDialog = null;
    if (dialog.open) dialog.close();
    syncDialogClasses();
    if (options.restoreScroll !== false) instantScroll(state.x, state.y);
    if (options.restoreFocus !== false) {
      const opener = state.opener;
      if (opener?.isConnected && !opener.closest('dialog:not([open])')) opener.focus({ preventScroll: true });
    }
    schedule();
  }

  function closeDialog(dialog, options = {}) {
    if (!dialog?.open) return;
    const state = dialogs.get(dialog);
    if (!state) { dialog.close(); syncDialogClasses(); return; }
    if (options.immediate || reduce.matches || document.hidden) { finishDialog(dialog, options); return; }
    if (state.closing) return;
    state.closing = true;
    state.options = options;
    dialog.classList.add('is-closing');
    const styles = getComputedStyle(dialog);
    const toMs = value => value.trim().endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
    const times = property => styles[property].split(',').map(toMs).filter(Number.isFinite);
    const duration = Math.max(0, ...times('transitionDuration'), ...times('animationDuration'));
    state.timer = setTimeout(() => finishDialog(dialog, options), Math.min(1200, duration || 500));
  }

  function openDialog(dialog, trigger) {
    if (!dialog || typeof dialog.showModal !== 'function') return false;
    if (dialog.open) {
      const state = dialogs.get(dialog);
      if (state?.closing) {
        clearTimeout(state.timer);
        state.closing = false;
        dialog.classList.remove('is-closing');
      }
      return true;
    }
    let opener = trigger || document.activeElement;
    if (activeDialog?.open) {
      if (activeDialog.contains(opener)) opener = dialogs.get(activeDialog)?.opener || opener;
      closeDialog(activeDialog, { immediate: true, restoreFocus: false });
    }
    const state = { opener, x: window.scrollX, y: window.scrollY, closing: false, timer: 0, options: {} };
    dialogs.set(dialog, state);
    if (!scrollLock) scrollLock = { overflow: html.style.overflow };
    html.style.overflow = 'hidden';
    dialog.classList.remove('is-closing');
    try { dialog.showModal(); }
    catch (_) { syncDialogClasses(); return false; }
    activeDialog = dialog;
    syncDialogClasses();
    if (dialog.id === 'PaniMenu') {
      const current = dialog.querySelector('[data-pani-menu-image]');
      if (current) setMenuArt(current);
    }
    return true;
  }

  function setMenuArt(link) {
    const menu = link.closest('#PaniMenu');
    if (!menu) return;
    menu.querySelectorAll('[data-pani-menu-art]').forEach(art => { art.dataset.active = link.dataset.paniMenuImage; });
  }

  function setTab(root, key, focus = false) {
    const state = tabsets.get(root);
    if (!state || !state.panels.some(panel => panel.dataset.paniPanel === key)) return;
    state.controls.forEach(tab => {
      const active = tab.dataset.paniTab === key;
      tab.classList.toggle('is-active', active);
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      if (active && focus) tab.focus({ preventScroll: true });
    });
    state.panels.forEach(panel => {
      const active = panel.dataset.paniPanel === key;
      panel.hidden = !active;
      panel.classList.toggle('is-active', active);
      if ('inert' in panel) panel.inert = !active;
    });
  }

  function mountTabs(root) {
    if (tabsets.has(root)) return;
    const controls = [...root.querySelectorAll('[data-pani-tab]')].filter(tab => tab.closest('[data-pani-tabs]') === root);
    const panels = [...root.querySelectorAll('[data-pani-panel]')].filter(panel => panel.closest('[data-pani-tabs]') === root);
    if (!controls.length || !panels.length) return;
    let list = root.querySelector('[data-pani-tablist], [role="tablist"]') || controls[0].parentElement;
    if (list === root || panels.some(panel => list.contains(panel))) {
      list = document.createElement('div');
      list.className = 'pani-tabs-controls';
      controls[0].before(list);
      controls.forEach(tab => list.append(tab));
    }
    list.setAttribute('role', 'tablist');
    const id = root.id || `pani-tabs-${++tabSequence}`;
    controls.forEach((tab, index) => {
      const panel = panels.find(item => item.dataset.paniPanel === tab.dataset.paniTab);
      if (!panel) return;
      tab.id ||= `${id}-tab-${index}`;
      panel.id ||= `${id}-panel-${index}`;
      tab.setAttribute('role', 'tab');
      if (tab.tagName === 'BUTTON') tab.type = 'button';
      tab.setAttribute('aria-controls', panel.id);
      panel.setAttribute('role', 'tabpanel');
      panel.setAttribute('aria-labelledby', tab.id);
      if (!panel.hasAttribute('tabindex')) panel.tabIndex = 0;
    });
    tabsets.set(root, { controls, panels });
    const selected = controls.find(tab => tab.getAttribute('aria-selected') === 'true' || tab.classList.contains('is-active')) || controls[0];
    setTab(root, selected.dataset.paniTab);
  }

  function videoButtons(video, state) {
    const playing = !video.paused && !video.ended;
    video.dataset.paniVideoState = playing ? 'playing' : reduce.matches && !state.explicitPlay ? 'poster' : 'paused';
    state.controls.forEach(button => {
      const label = playing ? button.dataset.paniPauseLabel || 'Pause video' : button.dataset.paniPlayLabel || 'Play video';
      button.setAttribute('aria-pressed', String(playing));
      button.setAttribute('aria-label', label);
      button.setAttribute('aria-controls', video.id);
      const text = button.querySelector('[data-pani-video-label]');
      if (text) text.textContent = label;
      else if (!button.children.length) button.textContent = label;
    });
  }

  function wantsVideo(state) {
    const frame = state.element?.closest('[data-pani-frame]');
    return (!frame || frame.classList.contains('is-active')) && !destroyed && !state.disposed && state.inView && !document.hidden && !state.userPaused && (!reduce.matches || state.explicitPlay);
  }

  function pauseVideo(video, state, poster = false) {
    state.version += 1;
    state.pending = false;
    video.pause();
    if (poster && state.loaded) video.load();
    videoButtons(video, state);
  }

  function syncVideo(video, state) {
    if (!wantsVideo(state)) { pauseVideo(video, state); return; }
    if (!state.loaded) {
      video.querySelectorAll('source[data-src]').forEach(source => { source.src = source.dataset.src; });
      state.loaded = true;
      video.load();
    }
    if (state.pending || !video.paused) { videoButtons(video, state); return; }
    const version = ++state.version;
    state.pending = true;
    let playback;
    try { playback = video.play(); }
    catch (_) { state.pending = false; videoButtons(video, state); return; }
    Promise.resolve(playback).then(() => {
      if (!wantsVideo(state)) video.pause();
      if (state.version === version) videoButtons(video, state);
    }).catch(() => {
      if (state.version === version) {
        video.dataset.paniVideoState = 'paused';
        videoButtons(video, state);
      }
    }).finally(() => { if (state.version === version) state.pending = false; });
  }

  function resolveVideo(button) {
    const id = button.getAttribute('aria-controls') || button.dataset.paniVideoToggle;
    if (id) {
      const video = document.getElementById(id.replace(/^#/, ''));
      if (video?.matches('video[data-pani-video]')) return video;
    }
    let parent = button.parentElement;
    while (parent && parent !== document.body) {
      const matches = parent.querySelectorAll('video[data-pani-video]');
      if (matches.length === 1) return matches[0];
      parent = parent.parentElement;
    }
    return videos.size === 1 ? videos.keys().next().value : null;
  }

  function mountVideo(video) {
    if (videos.has(video)) return;
    video.id ||= `pani-video-${++videoSequence}`;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.preload = 'none';
    const local = new AbortController();
    const state = { element: video, controller: local, controls: [], loaded: false, inView: false,
      userPaused: false, explicitPlay: false, pending: false, version: 0 };
    videos.set(video, state);
    ['play', 'pause', 'ended'].forEach(type => video.addEventListener(type, () => videoButtons(video, state), { signal: local.signal }));
    if (videoObserver) videoObserver.observe(video);
    else {
      const bounds = video.getBoundingClientRect();
      state.inView = bounds.bottom > 0 && bounds.top < innerHeight;
      syncVideo(video, state);
    }
  }

  function destroyVideo(video, state) {
    state.disposed = true;
    pauseVideo(video, state);
    state.controller.abort();
    videoObserver?.unobserve(video);
    videos.delete(video);
  }

  function resolveTrack(button) {
    const id = button.getAttribute('aria-controls') || button.dataset.paniPrev || button.dataset.paniNext;
    if (id) {
      const track = document.getElementById(id.replace(/^#/, ''));
      if (track?.matches('[data-pani-track]')) return track;
    }
    let parent = button.parentElement;
    while (parent && parent !== document.body) {
      const matches = parent.querySelectorAll('[data-pani-track]');
      if (matches.length === 1) return matches[0];
      parent = parent.parentElement;
    }
    return tracks.size === 1 ? tracks.keys().next().value : null;
  }

  function snapPoints(track) {
    let cards = [...track.querySelectorAll('[data-pani-card]')];
    if (!cards.length) cards = [...track.children];
    const bounds = track.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    return [...new Set(cards.map(card => {
      const box = card.getBoundingClientRect();
      const align = getComputedStyle(card).scrollSnapAlign;
      const offset = align.includes('center') ? (track.clientWidth - box.width) / 2 : padding;
      return Math.round(clamp(box.left - bounds.left + track.scrollLeft - offset, 0, max));
    }))].sort((a, b) => a - b);
  }

  function moveTrack(track, direction) {
    const state = tracks.get(track);
    if (state?.loop && !state.motion) normalizeLoop(track, state);
    const points = snapPoints(track);
    const reference = state?.motion && Math.sign(state.motion.to - state.motion.from) === direction ? state.motion.to : track.scrollLeft;
    const next = direction > 0 ? points.find(point => point > reference + 3)
      : [...points].reverse().find(point => point < reference - 3);
    const left = next ?? clamp(track.scrollLeft + direction * track.clientWidth * 0.85, 0, track.scrollWidth - track.clientWidth);
    if (state?.loop) animateTrack(track, state, left);
    else track.scrollTo({ left, behavior: reduce.matches ? 'instant' : 'smooth' });
  }

  function loopCards(track) {
    return [...track.children].filter(card => card.hasAttribute('data-pani-loop-index'));
  }

  function promoteCard(track, state, index, target) {
    const source = state.loop.sources[index];
    if (!source || source === target || source.contains(document.activeElement)) return;
    const left = track.scrollLeft;
    const marker = document.createComment('');
    target.replaceWith(marker);
    source.replaceWith(target);
    marker.replaceWith(source);
    track.scrollLeft = left;
  }

  function promoteLoop(track, state) {
    const bounds = track.getBoundingClientRect();
    const cards = loopCards(track);
    state.loop.sources.forEach((source, index) => {
      let best = source;
      let score = -Infinity;
      cards.filter(card => Number(card.dataset.paniLoopIndex) === index).forEach(card => {
        const box = card.getBoundingClientRect();
        const overlap = Math.max(0, Math.min(box.right, bounds.right) - Math.max(box.left, bounds.left));
        const rank = overlap * 1000 - Math.abs((box.left + box.right - bounds.left - bounds.right) / 2);
        if (rank > score) { score = rank; best = card; }
      });
      promoteCard(track, state, index, best);
    });
  }

  function measureLoop(track, state) {
    if (track.clientWidth <= 0) return;
    const cards = loopCards(track);
    const start = state.loop.copies * state.loop.sources.length;
    const current = cards[start];
    const following = cards[start + state.loop.sources.length];
    if (!current || !following) return;
    const bounds = track.getBoundingClientRect();
    const padding = parseFloat(getComputedStyle(track).paddingLeft) || 0;
    const box = current.getBoundingClientRect();
    const scale = track.offsetWidth > 0 ? bounds.width / track.offsetWidth : 1;
    state.loop.home = (box.left - bounds.left) / (scale || 1) + track.scrollLeft - padding;
    state.loop.cycle = (following.offsetLeft - current.offsetLeft) || (following.getBoundingClientRect().left - box.left) / (scale || 1);
    state.loop.width = track.clientWidth;
  }

  function refreshLoop(track, state) {
    cancelTrackMotion(track, state);
    const ratio = state.loop.cycle ? (track.scrollLeft - state.loop.home) / state.loop.cycle : 0;
    measureLoop(track, state);
    const snap = track.style.scrollSnapType;
    const behavior = track.style.scrollBehavior;
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    track.scrollLeft = state.loop.home + ratio * state.loop.cycle;
    normalizeLoop(track, state);
    track.style.scrollSnapType = snap;
    track.style.scrollBehavior = behavior;
  }

  function normalizeLoop(track, state) {
    const loop = state.loop;
    if (!loop?.cycle) return;
    const offset = ((track.scrollLeft - loop.home) % loop.cycle + loop.cycle) % loop.cycle;
    const left = loop.home + (loop.cycle - offset < 1 ? 0 : offset);
    const delta = left - track.scrollLeft;
    if (Math.abs(delta) > 1) {
      const snap = track.style.scrollSnapType;
      const behavior = track.style.scrollBehavior;
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
      track.scrollLeft = left;
      if (state.drag) state.drag.left += delta;
      track.style.scrollSnapType = snap;
      track.style.scrollBehavior = behavior;
    }
    promoteLoop(track, state);
  }

  function cancelTrackMotion(track, state, restore = true) {
    if (!state.motion) return;
    cancelAnimationFrame(state.motion.raf);
    state.motion = null;
    if (restore) {
      track.style.scrollSnapType = state.snap;
      track.style.scrollBehavior = state.behavior;
    }
  }

  function animateTrack(track, state, left) {
    cancelTrackMotion(track, state, false);
    track.style.scrollSnapType = 'none';
    track.style.scrollBehavior = 'auto';
    const finish = () => {
      state.motion = null;
      normalizeLoop(track, state);
      track.style.scrollSnapType = state.snap;
      track.style.scrollBehavior = state.behavior;
      schedule();
    };
    if (reduce.matches || document.hidden || Math.abs(track.scrollLeft - left) < 1) {
      track.scrollLeft = left;
      finish();
      return;
    }
    const motion = { from: track.scrollLeft, to: left, start: performance.now(), raf: 0 };
    state.motion = motion;
    const tick = now => {
      if (state.motion !== motion || destroyed || !track.isConnected) return;
      const progress = clamp((now - motion.start) / 800);
      const eased = progress < 0.5 ? 8 * progress ** 4 : 1 - (-2 * progress + 2) ** 4 / 2;
      track.scrollLeft = motion.from + (motion.to - motion.from) * eased;
      promoteLoop(track, state);
      if (progress < 1) motion.raf = requestAnimationFrame(tick);
      else finish();
    };
    motion.raf = requestAnimationFrame(tick);
  }

  function mountLoop(track, state) {
    const sources = [...track.children].filter(card => !card.hasAttribute('data-pani-loop-clone'));
    if (sources.length < 2) return;
    const widths = sources.reduce((sum, card) => sum + card.getBoundingClientRect().width, 0);
    const copies = Math.max(2, Math.ceil(track.clientWidth / Math.max(1, widths)));
    state.loop = { sources, copies, home: 0, cycle: 0 };
    sources.forEach((card, index) => card.setAttribute('data-pani-loop-index', String(index)));
    const duplicate = card => {
      const clone = card.cloneNode(true);
      clone.setAttribute('data-pani-loop-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      clone.inert = true;
      [clone, ...clone.querySelectorAll('[id]')].forEach(element => element.removeAttribute('id'));
      clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(element => { element.tabIndex = -1; });
      return clone;
    };
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (let copy = 0; copy < copies; copy += 1) sources.forEach(card => {
      before.append(duplicate(card));
      after.append(duplicate(card));
    });
    track.insertBefore(before, sources[0]);
    track.append(after);
    measureLoop(track, state);
    const behavior = track.style.scrollBehavior;
    const snap = track.style.scrollSnapType;
    track.style.scrollBehavior = 'auto';
    track.style.scrollSnapType = 'none';
    track.scrollLeft = state.loop.home;
    promoteLoop(track, state);
    track.style.scrollBehavior = behavior;
    track.style.scrollSnapType = snap;
  }

  function loopProxy(track, state, event) {
    if (!state.loop) return null;
    const hit = element => {
      const bounds = element.getBoundingClientRect();
      return event.clientX >= bounds.left && event.clientX <= bounds.right && event.clientY >= bounds.top && event.clientY <= bounds.bottom;
    };
    const clone = loopCards(track).find(card => card.hasAttribute('data-pani-loop-clone') && hit(card));
    if (!clone) return null;
    const actions = [...clone.querySelectorAll('a[href], button, [role="button"]')];
    if (clone.matches('a[href], button, [role="button"]')) actions.unshift(clone);
    const index = actions.findIndex(hit);
    const source = state.loop.sources[Number(clone.dataset.paniLoopIndex)];
    if (index < 0 || !source) return null;
    const originals = [...source.querySelectorAll('a[href], button, [role="button"]')];
    if (source.matches('a[href], button, [role="button"]')) originals.unshift(source);
    promoteCard(track, state, Number(clone.dataset.paniLoopIndex), clone);
    return originals[index] || null;
  }

  function endDrag(track, state, snap = true) {
    const drag = state.drag;
    if (!drag) return;
    state.drag = null;
    if (track.hasPointerCapture?.(drag.pointerId)) track.releasePointerCapture(drag.pointerId);
    if (!drag.dragging) return;
    state.suppressClickUntil = performance.now() + 500;
    track.style.scrollSnapType = state.snap;
    track.style.scrollBehavior = state.behavior;
    track.classList.remove('is-dragging');
    if (snap) {
      const points = snapPoints(track);
      if (points.length) {
        const left = points.reduce((best, point) => Math.abs(point - track.scrollLeft) < Math.abs(best - track.scrollLeft) ? point : best, points[0]);
        if (state.loop) animateTrack(track, state, left);
        else track.scrollTo({ left, behavior: reduce.matches ? 'instant' : 'smooth' });
      }
    }
    schedule();
  }

  function destroyTrack(track, state) {
    endDrag(track, state, false);
    cancelTrackMotion(track, state);
    state.controller.abort();
    if (state.loop) {
      loopCards(track).filter(card => card.hasAttribute('data-pani-loop-clone')).forEach(card => card.remove());
      state.loop.sources.forEach(card => { card.removeAttribute('data-pani-loop-index'); track.append(card); });
    }
    track.style.touchAction = state.touchAction;
    tracks.delete(track);
  }

  function mountTrack(track) {
    if (tracks.has(track)) return;
    const local = new AbortController();
    const state = { controller: local, controls: [], drag: null, proxy: null, motion: null, loop: null, suppressClickUntil: 0,
      touchAction: track.style.touchAction, snap: track.hasAttribute('data-pani-loop') ? 'none' : track.style.scrollSnapType, behavior: track.style.scrollBehavior };
    tracks.set(track, state);
    track.style.touchAction = 'pan-y pinch-zoom';
    if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
    if (track.hasAttribute('data-pani-loop')) {
      track.style.scrollSnapType = 'none';
      mountLoop(track, state);
    }
    const on = (type, callback, options = {}) => track.addEventListener(type, callback, { ...options, signal: local.signal });
    on('pointerdown', event => {
      state.suppressClickUntil = 0;
      cancelTrackMotion(track, state);
      const proxy = event.isPrimary && event.button === 0 ? loopProxy(track, state, event) : null;
      state.proxy = proxy ? { action: proxy, x: event.clientX, y: event.clientY, pointerId: event.pointerId } : null;
      const control = event.target.closest('button, input, select, textarea, summary, [contenteditable="true"]');
      const card = event.target.closest('[data-pani-loop-index]');
      if (!event.isPrimary || event.button !== 0 || track.scrollWidth <= track.clientWidth + 1 ||
        (control && control !== card)) return;
      state.drag = { pointerId: event.pointerId, x: event.clientX, y: event.clientY, left: track.scrollLeft, dragging: false };
    });
    on('pointermove', event => {
      if (state.proxy && (Math.abs(event.clientX - state.proxy.x) > 8 || Math.abs(event.clientY - state.proxy.y) > 8)) state.proxy = null;
      const drag = state.drag;
      if (!drag || drag.pointerId !== event.pointerId) return;
      const x = event.clientX - drag.x;
      const y = event.clientY - drag.y;
      if (!drag.dragging) {
        if (Math.abs(y) > 8 && Math.abs(y) > Math.abs(x)) { state.drag = null; return; }
        if (Math.abs(x) < 8 || Math.abs(x) < Math.abs(y) * 1.2) return;
        drag.dragging = true;
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
        track.classList.add('is-dragging');
        track.setPointerCapture?.(event.pointerId);
      }
      if (event.cancelable) event.preventDefault();
      track.scrollLeft = drag.left - x;
    }, { passive: false });
    on('pointerup', event => {
      if (state.proxy?.pointerId === event.pointerId && !state.drag?.dragging) {
        state.proxy.action.click();
        state.suppressClickUntil = performance.now() + 500;
      }
      state.proxy = null;
      if (state.drag?.pointerId === event.pointerId) endDrag(track, state);
    });
    on('pointercancel', event => { state.proxy = null; if (state.drag?.pointerId === event.pointerId) endDrag(track, state); });
    on('lostpointercapture', () => endDrag(track, state));
    on('pointerleave', () => { if (!state.drag?.dragging) state.drag = null; });
    on('dragstart', event => { if (state.drag) event.preventDefault(); });
    on('click', event => {
      if (event.detail !== 0 && performance.now() < state.suppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
        state.suppressClickUntil = 0;
      }
    }, { capture: true });
    on('scroll', schedule, { passive: true });
    on('wheel', () => cancelTrackMotion(track, state), { passive: true });
    on('scrollend', () => { if (state.loop && !state.motion && !state.drag?.dragging) normalizeLoop(track, state); });
    on('keydown', event => {
      if (event.target !== track || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      cancelTrackMotion(track, state);
      if (event.key.startsWith('Arrow')) moveTrack(track, event.key === 'ArrowRight' ? 1 : -1);
      else if (state.loop) {
        const points = snapPoints(track).filter(point => point >= state.loop.home - 1 && point < state.loop.home + state.loop.cycle - 1);
        animateTrack(track, state, event.key === 'Home' ? state.loop.home : points.at(-1) || state.loop.home);
      } else track.scrollTo({ left: event.key === 'Home' ? 0 : track.scrollWidth - track.clientWidth, behavior: reduce.matches ? 'instant' : 'smooth' });
    });
  }

  function finishAccordion(details, state) {
    const animation = state.animation;
    state.animation = null;
    animation?.cancel();
    details.open = state.open;
    details.style.height = state.height;
    details.style.overflow = state.overflow;
  }

  function animateAccordion(details, open) {
    let state = accordions.get(details);
    if (!state) {
      state = { animation: null, open: details.open, height: details.style.height, overflow: details.style.overflow };
      accordions.set(details, state);
    }
    if (state.animation && state.open === open) return;
    const from = details.getBoundingClientRect().height;
    state.open = open;
    const previous = state.animation;
    state.animation = null;
    previous?.cancel();
    if (reduce.matches || document.hidden || typeof details.animate !== 'function') { finishAccordion(details, state); return; }
    const summary = details.querySelector(':scope > summary');
    if (!summary) { finishAccordion(details, state); return; }
    details.open = true;
    details.style.height = state.height;
    let to = details.getBoundingClientRect().height;
    if (!open) {
      details.open = false;
      to = details.getBoundingClientRect().height;
      details.open = true;
    }
    details.style.height = `${from}px`;
    details.style.overflow = 'hidden';
    const animation = details.animate([{ height: `${from}px` }, { height: `${to}px` }], {
      duration: 480, easing: 'cubic-bezier(.25,1,.5,1)', fill: 'both'
    });
    state.animation = animation;
    animation.onfinish = () => { if (state.animation === animation) finishAccordion(details, state); };
  }

  function closeAccordionSiblings(details) {
    const group = details.closest('[data-pani-accordion]');
    if (!group) return;
    group.querySelectorAll('details').forEach(other => {
      if (other !== details && other.closest('[data-pani-accordion]') === group &&
        (other.open || accordions.get(other)?.open)) animateAccordion(other, false);
    });
  }

  function mount(root = document) {
    header = document.querySelector('[data-pani-header]');
    hero = document.body.classList.contains('sf-pani-home') ? document.querySelector('[data-pani-hero]') : null;
    for (const title of select(root, '[data-pani-title]')) wrapTitle(title);
    for (const element of select(root, '[data-pani-reveal], [data-pani-title]')) {
      if (reduce.matches || !revealObserver || element.classList.contains('is-visible')) visible(element);
      else revealObserver.observe(element);
    }
    for (const element of select(root, '[data-pani-parallax]')) {
      if (!parallaxes.has(element)) parallaxes.set(element, {
        current: 0, translate: element.style.translate,
        anchor: element.closest('[data-pani-parallax-container]') || element.parentElement || element
      });
    }
    for (const story of select(root, '[data-pani-story]')) {
      if (stories.has(story)) continue;
      stories.set(story, { index: -1, frames: [...story.querySelectorAll('[data-pani-frame]')], steps: [...story.querySelectorAll('[data-pani-step]')] });
      setFrame(story, 0);
    }
    for (const track of select(root, '[data-pani-track]')) mountTrack(track);
    for (const tabs of select(root, '[data-pani-tabs]')) mountTabs(tabs);
    for (const video of select(root, 'video[data-pani-video]')) mountVideo(video);
    document.querySelectorAll('[data-pani-video-toggle]').forEach(button => {
      const video = resolveVideo(button);
      const state = videos.get(video);
      if (state && !state.controls.includes(button)) { state.controls.push(button); videoButtons(video, state); }
    });
    document.querySelectorAll('[data-pani-prev], [data-pani-next]').forEach(button => {
      const track = resolveTrack(button);
      const state = tracks.get(track);
      if (state && !state.controls.includes(button)) state.controls.push(button);
    });
    syncDialogClasses();
    schedule();
  }

  listen(document, 'click', event => {
    if (!(event.target instanceof Element)) return;
    const toggle = event.target.closest('[data-pani-menu-toggle]');
    if (toggle) {
      const dialog = document.getElementById('PaniMenu');
      if (dialog?.open && !dialogs.get(dialog)?.closing) closeDialog(dialog);
      else if (!openDialog(dialog, toggle)) return;
      event.preventDefault();
      return;
    }
    const shop = event.target.closest('[data-pani-open-shop]');
    if (shop) {
      if (openDialog(document.getElementById('PaniShop'), shop)) event.preventDefault();
      return;
    }
    const detail = event.target.closest('[data-pani-detail]');
    if (detail) {
      const dialog = document.getElementById(detail.dataset.paniDetail);
      if (isPaniDialog(dialog) && openDialog(dialog, detail)) event.preventDefault();
      return;
    }
    const close = event.target.closest('[data-pani-close]');
    if (close) {
      const dialog = close.closest('#PaniMenu, #PaniShop, .pani-product-dialog');
      if (dialog) { event.preventDefault(); closeDialog(dialog); }
      return;
    }
    const videoToggle = event.target.closest('[data-pani-video-toggle]');
    if (videoToggle) {
      const video = resolveVideo(videoToggle);
      const state = videos.get(video);
      if (!state) return;
      event.preventDefault();
      if (!video.paused || state.pending) {
        state.userPaused = true;
        state.explicitPlay = false;
        pauseVideo(video, state);
      } else {
        state.userPaused = false;
        state.explicitPlay = true;
        const bounds = video.getBoundingClientRect();
        state.inView = bounds.bottom > 0 && bounds.top < innerHeight;
        syncVideo(video, state);
      }
      return;
    }
    const tab = event.target.closest('[data-pani-tab]');
    if (tab) {
      const root = tab.closest('[data-pani-tabs]');
      if (tabsets.has(root)) { event.preventDefault(); setTab(root, tab.dataset.paniTab); }
      return;
    }
    const arrow = event.target.closest('[data-pani-prev], [data-pani-next]');
    if (arrow) {
      const track = resolveTrack(arrow);
      if (track) { event.preventDefault(); moveTrack(track, arrow.hasAttribute('data-pani-next') ? 1 : -1); }
      return;
    }
    const step = event.target.closest('[data-pani-step]');
    if (step) {
      const story = step.closest('[data-pani-story]');
      const state = stories.get(story);
      if (!state) return;
      event.preventDefault();
      const value = Number(step.dataset.paniStep);
      const index = clamp(Number.isFinite(value) && step.dataset.paniStep !== '' ? value : state.steps.indexOf(step), 0, state.frames.length - 1);
      setFrame(story, index);
      const range = Math.max(0, story.getBoundingClientRect().height - innerHeight);
      if (range > 1) window.scrollTo({ top: window.scrollY + story.getBoundingClientRect().top + range * index / Math.max(1, state.frames.length - 1), behavior: reduce.matches ? 'instant' : 'smooth' });
      return;
    }
    const summary = event.target.closest('[data-pani-accordion] details > summary');
    if (summary && !event.target.closest('a, button, input, select, textarea')) {
      event.preventDefault();
      const details = summary.parentElement;
      const open = !(accordions.get(details)?.animation ? accordions.get(details).open : details.open);
      if (open) closeAccordionSiblings(details);
      animateAccordion(details, open);
      return;
    }
    const menu = event.target.closest('#PaniMenu');
    if (menu && event.target.closest('a[href]')) closeDialog(menu, { immediate: true, restoreFocus: false, restoreScroll: false });
    if (isPaniDialog(event.target)) closeDialog(event.target);
  });

  listen(document, 'keydown', event => {
    const tab = event.target.closest?.('[data-pani-tab]');
    if (!tab || !['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
    const root = tab.closest('[data-pani-tabs]');
    const state = tabsets.get(root);
    if (!state) return;
    event.preventDefault();
    let index = state.controls.indexOf(tab);
    if (event.key === 'Home') index = 0;
    else if (event.key === 'End') index = state.controls.length - 1;
    else index = (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + state.controls.length) % state.controls.length;
    setTab(root, state.controls[index].dataset.paniTab, true);
  });

  listen(document, 'submit', event => {
    if (!event.target.matches?.('form[data-sf-product-form]')) return;
    const dialog = event.target.closest('#PaniShop, .pani-product-dialog');
    if (dialog?.open) closeDialog(dialog, { immediate: true, restoreFocus: false, restoreScroll: false });
  }, { capture: true });
  listen(document, 'click', event => {
    if (event.target.closest?.('[data-cart-open]') && activeDialog?.open)
      closeDialog(activeDialog, { immediate: true, restoreFocus: false, restoreScroll: false });
  }, { capture: true });
  listen(document, 'cancel', event => {
    if (!isPaniDialog(event.target)) return;
    event.preventDefault();
    closeDialog(event.target);
  }, { capture: true });
  listen(document, 'close', event => {
    if (!isPaniDialog(event.target)) return;
    const state = dialogs.get(event.target);
    if (state?.closing) clearTimeout(state.timer);
    event.target.classList.remove('is-closing');
    if (activeDialog === event.target) activeDialog = null;
    syncDialogClasses();
  }, { capture: true });
  listen(document, 'pointerover', event => {
    if (!finePointer.matches) return;
    const link = event.target.closest?.('[data-pani-menu-image]');
    if (link) setMenuArt(link);
  });
  listen(document, 'focusin', event => {
    const link = event.target.closest?.('[data-pani-menu-image]');
    if (link) setMenuArt(link);
  });
  listen(document, 'toggle', event => {
    const details = event.target;
    if (details.matches?.('[data-pani-accordion] details') && details.open && accordions.get(details)?.open !== false) closeAccordionSiblings(details);
  }, { capture: true });
  listen(window, 'scroll', schedule, { passive: true });
  listen(window, 'resize', schedule, { passive: true });
  listen(window, 'pageshow', schedule);
  listen(document, 'visibilitychange', () => {
    html.classList.toggle('pani-page-hidden', document.hidden);
    if (document.hidden) {
      cancelAnimationFrame(raf);
      raf = 0;
      for (const [track, state] of tracks) {
        cancelTrackMotion(track, state);
        state.proxy = null;
        if (state.loop) normalizeLoop(track, state);
      }
      for (const [details, state] of accordions) if (state.animation) finishAccordion(details, state);
      for (const [video, state] of videos) pauseVideo(video, state);
      if (activeDialog && dialogs.get(activeDialog)?.closing) finishDialog(activeDialog, dialogs.get(activeDialog).options);
    } else {
      for (const element of [...waiting]) if (element.isConnected) visible(element); else waiting.delete(element);
      for (const [video, state] of videos) syncVideo(video, state);
      schedule();
    }
  });
  listen(reduce, 'change', () => {
    if (reduce.matches) {
      finishIntro();
      for (const [track, state] of tracks) {
        const target = state.motion?.to;
        cancelTrackMotion(track, state);
        if (state.loop && target !== undefined) {
          const behavior = track.style.scrollBehavior;
          track.style.scrollBehavior = 'auto';
          track.scrollLeft = target;
          normalizeLoop(track, state);
          track.style.scrollBehavior = behavior;
        }
      }
      document.querySelectorAll('[data-pani-reveal], [data-pani-title]').forEach(visible);
      for (const [element, state] of parallaxes) {
        state.current = 0;
        element.style.translate = state.translate;
        element.style.setProperty('--pani-parallax-y', '0%');
      }
      for (const [details, state] of accordions) if (state.animation) finishAccordion(details, state);
      for (const [video, state] of videos) {
        state.explicitPlay = false;
        pauseVideo(video, state, true);
      }
      if (activeDialog && dialogs.get(activeDialog)?.closing) finishDialog(activeDialog, dialogs.get(activeDialog).options);
    }
    if (!reduce.matches) for (const [video, state] of videos) syncVideo(video, state);
    schedule();
  });
  listen(finePointer, 'change', () => html.classList.toggle('pani-fine-pointer', finePointer.matches));
  listen(document, 'shopify:section:load', event => mount(event.target));
  listen(document, 'shopify:section:unload', event => {
    const root = event.target;
    for (const [track, state] of tracks) if (root.contains(track)) destroyTrack(track, state);
    for (const [element, state] of parallaxes) if (root.contains(element)) {
      element.style.translate = state.translate;
      parallaxes.delete(element);
    }
    for (const [story] of stories) if (root.contains(story)) stories.delete(story);
    for (const [tabs] of tabsets) if (root.contains(tabs)) tabsets.delete(tabs);
    for (const [video, state] of videos) if (root.contains(video)) destroyVideo(video, state);
    for (const [details, state] of accordions) if (root.contains(details)) { finishAccordion(details, state); accordions.delete(details); }
    select(root, '[data-pani-reveal], [data-pani-title]').forEach(element => { revealObserver?.unobserve(element); waiting.delete(element); });
    if (activeDialog && root.contains(activeDialog)) closeDialog(activeDialog, { immediate: true });
  });

  function finishIntro() {
    for (const timer of introTimers) clearTimeout(timer);
    introTimers.length = 0;
    introOverlay?.remove();
    introOverlay = null;
    html.classList.remove('pani-intro-animate');
  }

  function startIntro() {
    if (!hero || reduce.matches || window.Shopify?.designMode || window.scrollY > 10 || document.querySelector('.sf-home-contact-success, .sf-home-contact-errors')) return;
    introOverlay = document.createElement('div');
    introOverlay.className = 'pani-intro-overlay';
    introOverlay.setAttribute('aria-hidden', 'true');
    const wipe = document.createElement('div');
    wipe.className = 'pani-intro-wipe';
    const logo = document.createElement('div');
    logo.className = 'pani-intro-logo';
    const image = header?.querySelector('.pani-logo img');
    if (image) logo.append(image.cloneNode(true));
    else logo.textContent = 'Seaforia';
    introOverlay.append(wipe, logo);
    document.body.append(introOverlay);
    html.classList.add('pani-intro-animate');
    listen(introOverlay, 'click', finishIntro);
    introTimers.push(setTimeout(() => { introOverlay?.remove(); introOverlay = null; }, 3800));
    introTimers.push(setTimeout(finishIntro, 7000));
  }

  function destroy() {
    destroyed = true;
    finishIntro();
    cancelAnimationFrame(raf);
    controller.abort();
    revealObserver?.disconnect();
    videoObserver?.disconnect();
    for (const [track, state] of tracks) destroyTrack(track, state);
    for (const [details, state] of accordions) finishAccordion(details, state);
    for (const [video, state] of videos) destroyVideo(video, state);
    for (const [element, state] of parallaxes) element.style.translate = state.translate;
    for (const [, state] of tabsets) state.panels.forEach(panel => { panel.hidden = false; if ('inert' in panel) panel.inert = false; });
    if (activeDialog) finishDialog(activeDialog, { restoreFocus: false });
    document.querySelectorAll('[data-pani-reveal], [data-pani-title]').forEach(element => element.classList.add('is-visible'));
    html.classList.remove('pani-enhanced', 'pani-fine-pointer', 'pani-page-hidden');
    waiting.clear();
    tracks.clear();
    stories.clear();
    tabsets.clear();
    videos.clear();
    parallaxes.clear();
    accordions.clear();
    dialogs.clear();
  }

  window.__sfPaniUI = { refresh: mount, destroy };
  html.classList.add('pani-enhanced');
  html.classList.toggle('pani-fine-pointer', finePointer.matches);
  html.classList.toggle('pani-page-hidden', document.hidden);
  mount();
  startIntro();
  listen(document, 'keydown', event => { if (event.key === 'Escape' && introOverlay) finishIntro(); });
})();
